import { fetchApi } from '@/services/api-client';

const IT_ASSETS_PATH = '/it-assets';

export type ItAssetDeviceType =
  | 'laptop'
  | 'desktop'
  | 'macbook'
  | 'mac_mini'
  | 'imac'
  | 'tablet'
  | 'ipad'
  | 'monitor'
  | 'printer'
  | 'projector'
  | 'network'
  | 'server'
  | 'phone'
  | 'other';
export type ItAssetStatus = 'in_use' | 'available' | 'repair' | 'storage' | 'retired' | 'lost';
export type ItAssetDepartment = 'it' | 'staff' | 'teacher' | 'student' | 'ia_specialist' | 'shared' | 'other';
export type ItAssetCredentialKind = 'product_key' | 'local' | 'admin' | 'microsoft' | 'email' | 'icloud' | 'other';
export type ItAssetEventType = 'created' | 'assigned' | 'returned' | 'status_changed';

export const IT_ASSET_DEVICE_TYPES: ItAssetDeviceType[] = [
  'laptop',
  'desktop',
  'macbook',
  'mac_mini',
  'imac',
  'tablet',
  'ipad',
  'monitor',
  'printer',
  'projector',
  'network',
  'server',
  'phone',
  'other',
];
export const IT_ASSET_STATUSES: ItAssetStatus[] = ['in_use', 'available', 'repair', 'storage', 'retired', 'lost'];
export const IT_ASSET_DEPARTMENTS: ItAssetDepartment[] = [
  'it',
  'staff',
  'teacher',
  'student',
  'ia_specialist',
  'shared',
  'other',
];
export const IT_ASSET_CREDENTIAL_KINDS: ItAssetCredentialKind[] = [
  'product_key',
  'local',
  'admin',
  'microsoft',
  'email',
  'icloud',
  'other',
];

export interface ItAssetPersonRef {
  id: string;
  fullName: string | null;
  role: string;
}

export interface ItAssetRoomRef {
  id: string;
  name: string;
  code: string | null;
}

export interface ItAssetSummaryRow {
  id: string;
  legacyAssetId: string | null;
  assetTag: string;
  deviceType: ItAssetDeviceType;
  deviceTypeOther: string | null;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  status: ItAssetStatus;
  department: ItAssetDepartment | null;
  assignedUser: ItAssetPersonRef | null;
  room: ItAssetRoomRef | null;
  assignedToText: string | null;
  photoUrl: string | null;
  modelPhotoUrl: string | null;
  labelPayload: string;
  updatedAt: string;
}

export interface ItAssetList {
  items: ItAssetSummaryRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ItAssetCredential {
  id: string;
  kind: ItAssetCredentialKind;
  label: string | null;
  username: string | null;
  hint: string | null;
  hasSecret: boolean;
  updatedAt: string;
}

export interface ItAssetEvent {
  id: string;
  type: ItAssetEventType;
  fromUser: ItAssetPersonRef | null;
  toUser: ItAssetPersonRef | null;
  fromRoom: ItAssetRoomRef | null;
  toRoom: ItAssetRoomRef | null;
  fromStatus: ItAssetStatus | null;
  toStatus: ItAssetStatus | null;
  note: string | null;
  actor: ItAssetPersonRef | null;
  createdAt: string;
}

export interface ItAssetDetail extends ItAssetSummaryRow {
  libibCode: string | null;
  serviceTag: string | null;
  color: string | null;
  departmentOther: string | null;
  purchaseDate: string | null;
  warrantyUntil: string | null;
  costBaht: number | null;
  notes: string | null;
  credentials: ItAssetCredential[];
  events: ItAssetEvent[];
  createdAt: string;
}

export interface ItAssetSummary {
  total: number;
  byStatus: Record<ItAssetStatus, number>;
  unassigned: number;
  needsMatching: number;
  totalValue: number;
  valueByType: Partial<Record<ItAssetDeviceType, number>>;
  age: { lt1: number; y1to3: number; y3to5: number; gt5: number; unknown: number };
}

export type ItAssetSortField = 'assetTag' | 'deviceType' | 'brand' | 'status' | 'purchaseDate' | 'updatedAt';

export interface ItAssetFilters {
  q?: string;
  deviceType?: ItAssetDeviceType;
  status?: ItAssetStatus;
  department?: ItAssetDepartment;
  assignedUserId?: string;
  roomId?: string;
  unassigned?: boolean;
  needsMatching?: boolean;
  sort?: ItAssetSortField;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface ItAssetInput {
  assetTag: string;
  deviceType: ItAssetDeviceType;
  deviceTypeOther?: string | null;
  brand?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  serviceTag?: string | null;
  color?: string | null;
  libibCode?: string | null;
  department?: ItAssetDepartment | null;
  departmentOther?: string | null;
  status?: ItAssetStatus;
  assignedUserId?: string | null;
  roomId?: string | null;
  purchaseDate?: string | null;
  warrantyUntil?: string | null;
  costBaht?: number | null;
  notes?: string | null;
}

/** Assignment and status have their own endpoints; PATCH rejects them. */
export type ItAssetUpdateInput = Partial<Omit<ItAssetInput, 'status' | 'assignedUserId' | 'roomId'>>;

export interface ItAssetCredentialInput {
  kind: ItAssetCredentialKind;
  label?: string | null;
  username?: string | null;
  secret?: string | null;
  hint?: string | null;
}

export type BulkPhotoFailureCode = 'unsupported_format' | 'invalid_image' | 'save_failed';

export interface BulkPhotoResult {
  matched: Array<{ file: string; assetId: string; assetTag: string }>;
  unmatched: string[];
  ambiguous: Array<{ file: string; assetTags: string[] }>;
  failed: Array<{ file: string; code: BulkPhotoFailureCode }>;
}

export interface ItAssetModelRow {
  modelKey: string;
  brand: string | null;
  model: string;
  deviceCount: number;
  ownPhotoCount: number;
  photoId: string | null;
  photoUrl: string | null;
}

function toQuery(filters: ItAssetFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

// The API's largest allowed page.
const LIST_ALL_PAGE_SIZE = 200;

export const itAssetsApi = {
  list: (f: ItAssetFilters) => fetchApi<ItAssetList>(`${IT_ASSETS_PATH}${toQuery(f)}`),
  listAll: async (f: Omit<ItAssetFilters, 'page' | 'pageSize'>) => {
    const items: ItAssetSummaryRow[] = [];
    for (let page = 1; ; page += 1) {
      const res = await itAssetsApi.list({ ...f, page, pageSize: LIST_ALL_PAGE_SIZE });
      items.push(...res.items);
      if (res.items.length < LIST_ALL_PAGE_SIZE || items.length >= res.total) return items;
    }
  },
  summary: () => fetchApi<ItAssetSummary>(`${IT_ASSETS_PATH}/summary`),
  resolve: (code: string) => fetchApi<{ id: string }>(`${IT_ASSETS_PATH}/resolve?code=${encodeURIComponent(code)}`),
  get: (id: string) => fetchApi<ItAssetDetail>(`${IT_ASSETS_PATH}/${id}`),
  create: (data: ItAssetInput) => fetchApi<ItAssetDetail>(IT_ASSETS_PATH, json('POST', data)),
  update: (id: string, data: ItAssetUpdateInput) =>
    fetchApi<ItAssetDetail>(`${IT_ASSETS_PATH}/${id}`, json('PATCH', data)),
  assign: (id: string, data: { userId: string | null; roomId: string | null; note?: string }) =>
    fetchApi<ItAssetDetail>(`${IT_ASSETS_PATH}/${id}/assign`, json('POST', data)),
  changeStatus: (id: string, data: { status: ItAssetStatus; note?: string }) =>
    fetchApi<ItAssetDetail>(`${IT_ASSETS_PATH}/${id}/status`, json('POST', data)),
  remove: (id: string) => fetchApi<{ success: true }>(`${IT_ASSETS_PATH}/${id}`, { method: 'DELETE' }),
  uploadPhoto: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return fetchApi<{ photoUrl: string }>(`${IT_ASSETS_PATH}/${id}/photo`, { method: 'PUT', body: form });
  },
  removePhoto: (id: string) => fetchApi<{ success: true }>(`${IT_ASSETS_PATH}/${id}/photo`, { method: 'DELETE' }),
  modelPhotos: () => fetchApi<ItAssetModelRow[]>(`${IT_ASSETS_PATH}/model-photos`),
  setModelPhoto: (brand: string | null, model: string, file: File) => {
    const form = new FormData();
    if (brand) form.append('brand', brand);
    form.append('model', model);
    form.append('file', file);
    return fetchApi<{ id: string; photoUrl: string }>(`${IT_ASSETS_PATH}/model-photos`, { method: 'PUT', body: form });
  },
  removeModelPhoto: (photoId: string) =>
    fetchApi<{ success: true }>(`${IT_ASSETS_PATH}/model-photos/${photoId}`, { method: 'DELETE' }),
  bulkPhotos: (files: File[]) => {
    const form = new FormData();
    files.forEach(f => form.append('files', f, f.name));
    return fetchApi<BulkPhotoResult>(`${IT_ASSETS_PATH}/photos/bulk`, {
      method: 'POST',
      body: form,
      timeoutMs: 300_000,
    });
  },
  createCredential: (id: string, data: ItAssetCredentialInput) =>
    fetchApi<ItAssetCredential>(`${IT_ASSETS_PATH}/${id}/credentials`, json('POST', data)),
  updateCredential: (id: string, credId: string, data: Partial<ItAssetCredentialInput>) =>
    fetchApi<ItAssetCredential>(`${IT_ASSETS_PATH}/${id}/credentials/${credId}`, json('PATCH', data)),
  removeCredential: (id: string, credId: string) =>
    fetchApi<{ success: true }>(`${IT_ASSETS_PATH}/${id}/credentials/${credId}`, { method: 'DELETE' }),
  revealCredential: (id: string, credId: string) =>
    fetchApi<{ id: string; username: string | null; secret: string | null }>(
      `${IT_ASSETS_PATH}/${id}/credentials/${credId}/reveal`
    ),
};
